// Lightweight toast bus replacing the old lib `message` call sites.
// HeroUI's ToastProvider (built on react-aria's UNSTABLE toast API) renders
// children as nothing with react-aria-components 1.21.1, so toasts are hosted
// by our own ToastHost instead; the message.* API is unchanged.

export type ToastKind = 'success' | 'error' | 'warning' | 'info'

export interface ToastItem {
  id: number
  kind: ToastKind
  content: string
}

const TOAST_TTL = 3200

let seq = 0
let items: ToastItem[] = []
const listeners = new Set<(current: ToastItem[]) => void>()

function emit() {
  for (const listener of listeners) listener(items)
}

function push(kind: ToastKind, content: string) {
  const item: ToastItem = { id: ++seq, kind, content }
  items = [...items, item]
  emit()
  setTimeout(() => dismiss(item.id), TOAST_TTL)
}

export function dismiss(id: number) {
  if (!items.some((i) => i.id === id)) return
  items = items.filter((i) => i.id !== id)
  emit()
}

export function subscribeToasts(listener: (current: ToastItem[]) => void): () => void {
  listeners.add(listener)
  listener(items)
  return () => {
    listeners.delete(listener)
  }
}

export const message = {
  success: (content: string) => push('success', content),
  error: (content: string) => push('error', content),
  warning: (content: string) => push('warning', content),
  info: (content: string) => push('info', content),
}
